import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { sans, mono, green, red, coral, coralHover } from '../tokens';

const cardStyle = {
  background: '#FFFBF6', borderRadius: 12, padding: '40px 36px', width: 340,
  boxShadow: '0 4px 24px rgba(15, 42, 36, 0.08)', textAlign: 'center',
};
const inputStyle = (hasError) => ({
  width: '100%', padding: '10px 14px', fontSize: 14, fontFamily: sans,
  border: `1.5px solid ${hasError ? red : 'rgba(15, 42, 36, 0.15)'}`,
  borderRadius: 6, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s', textAlign: 'center', letterSpacing: 4,
  background: '#FFFBF6',
});
const btnStyle = (loading) => ({
  marginTop: 16, width: '100%', padding: '10px 0', fontSize: 14, fontWeight: 500,
  background: loading ? '#9EACA7' : coral, color: '#FFF8F1', border: 'none', borderRadius: 6,
  cursor: loading ? 'default' : 'pointer', fontFamily: sans, transition: 'background 0.2s',
});

// ─── MFA Verification Screen ─────────────────────────────────────────────────
function MfaVerify() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { data: factors } = await supabase.auth.mfa.listFactors();
    const totp = factors?.totp?.[0];
    if (!totp) { setError('No MFA factor found'); setLoading(false); return; }

    const { data: challenge, error: chalErr } = await supabase.auth.mfa.challenge({ factorId: totp.id });
    if (chalErr) { setError(chalErr.message); setLoading(false); return; }

    const { error: verErr } = await supabase.auth.mfa.verify({
      factorId: totp.id,
      challengeId: challenge.id,
      code: code.trim(),
    });
    if (verErr) {
      setError('Invalid code. Please try again.');
      setLoading(false);
      return;
    }
    // Verified -- reload to pick up AAL2 session
    window.location.reload();
  };

  return (
    <form onSubmit={handleVerify} style={cardStyle}>
      <div style={{ textAlign: 'center', marginBottom: 4 }}>
        <img src="/finkloz-logo.svg" alt="FinKloz" style={{ height: 48 }} />
      </div>
      <div style={{ fontSize: 12, color: '#9E9E9E', marginBottom: 24, textAlign: 'center' }}>
        Enter the 6-digit code from your authenticator app
      </div>
      <input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="000000"
        autoFocus
        maxLength={6}
        style={{ ...inputStyle(!!error), fontSize: 24, fontFamily: mono, padding: '14px' }}
      />
      {error && <div style={{ fontSize: 11, color: red, marginTop: 6 }}>{error}</div>}
      <button type="submit" disabled={loading || code.length < 6} style={btnStyle(loading || code.length < 6)}>
        {loading ? 'Verifying...' : 'Verify'}
      </button>
      <button type="button" onClick={() => supabase.auth.signOut()} style={{
        marginTop: 10, background: 'none', border: 'none', color: '#9E9E9E',
        fontSize: 11, cursor: 'pointer', fontFamily: sans,
      }}>
        Sign out
      </button>
    </form>
  );
}

// ─── Main Login Page ─────────────────────────────────────────────────────────
export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsMfa, setNeedsMfa] = useState(false);

  // Check if user already has a session but needs MFA
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (data?.currentLevel === 'aal1' && data?.nextLevel === 'aal2') {
          setNeedsMfa(false); // MFA temporarily disabled — see App.js bypass
        }
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError(error.message); setLoading(false); return; }

    // Check if MFA is required
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (data?.currentLevel === 'aal1' && data?.nextLevel === 'aal2') {
      setNeedsMfa(true);
    }
    setLoading(false);
  };

  if (needsMfa) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAFA', fontFamily: sans }}>
        <MfaVerify />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#FFF8F1', fontFamily: sans,
    }}>
      <form onSubmit={handleSubmit} style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <img src="/finkloz-logo.svg" alt="FinKloz" style={{ height: 48 }} />
        </div>
        <div style={{ fontSize: 12, color: '#9E9E9E', marginBottom: 28, textAlign: 'center' }}>Sign in to continue</div>

        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Email" autoFocus required
          style={{ width: '100%', padding: '10px 14px', fontSize: 14, fontFamily: sans, border: '1px solid rgba(15,42,36,0.15)', borderRadius: 6, outline: 'none', boxSizing: 'border-box', marginBottom: 10, background: '#FFFBF6' }}
        />
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Password" required
          style={{ width: '100%', padding: '10px 14px', fontSize: 14, fontFamily: sans, border: `1px solid ${error ? red : 'rgba(15,42,36,0.15)'}`, borderRadius: 6, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s', background: '#FFFBF6' }}
        />

        {error && <div style={{ fontSize: 11, color: red, marginTop: 6, textAlign: 'left' }}>{error}</div>}

        <button type="submit" disabled={loading} style={btnStyle(loading)}>
          {loading ? 'Signing in...' : 'Sign in'}
        </button>

      </form>
    </div>
  );
}

// ─── MFA Enrollment (exported for use in settings) ───────────────────────────
export function MfaEnroll({ onComplete }) {
  const [qrUri, setQrUri] = useState(null);
  const [factorId, setFactorId] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      // Use direct REST to avoid supabase-js navigator.locks hangs
      const SB_URL = process.env.REACT_APP_SUPABASE_URL;
      const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const ref = SB_URL ? new URL(SB_URL).hostname.split('.')[0] : '';
      let token;
      try {
        const raw = localStorage.getItem(`sb-${ref}-auth-token`);
        if (raw) token = JSON.parse(raw)?.access_token;
      } catch { /* ignore */ }
      if (!token) { setError('Not authenticated'); return; }

      const headers = { 'apikey': SB_KEY, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      // Unenroll existing TOTP factors
      try {
        const fRes = await fetch(`${SB_URL}/auth/v1/user`, { headers });
        if (fRes.ok) {
          const userData = await fRes.json();
          const factors = userData?.factors || [];
          for (const f of factors) {
            if (f.factor_type === 'totp') {
              await supabase.auth.mfa.unenroll({ factorId: f.id }).catch(() => {});
            }
          }
        }
      } catch { /* ignore */ }

      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'FinKloz Auth' });
      if (error) { setError(error.message); return; }
      setQrUri(data.totp.qr_code);
      setFactorId(data.id);
    })();
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data: challenge, error: chalErr } = await supabase.auth.mfa.challenge({ factorId });
      if (chalErr) { setError(chalErr.message); setLoading(false); return; }

      const { error: verErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: code.trim(),
      });
      setLoading(false);
      if (verErr) { setError(verErr.message || 'Invalid code. Try again.'); setCode(''); return; }
      setSuccess(true);
      if (onComplete) onComplete();
    } catch (err) {
      setLoading(false);
      setError(err.message || 'Verification failed. Try again.');
      setCode('');
    }
  };

  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: green, marginBottom: 8 }}>MFA Enabled</div>
        <div style={{ fontSize: 12, color: '#757575' }}>
          Your account is now protected with two-factor authentication.
        </div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Set up two-factor authentication</div>
      <div style={{ fontSize: 11, color: '#757575', marginBottom: 16 }}>
        Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
      </div>
      {qrUri ? (
        <div style={{ marginBottom: 16 }}>
          <img src={qrUri} alt="MFA QR Code" style={{ width: 200, height: 200, borderRadius: 8 }} />
        </div>
      ) : (
        <div style={{ padding: 20, color: '#9E9E9E', fontSize: 12 }}>Loading QR code...</div>
      )}
      <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ fontSize: 11, color: '#757575', marginBottom: 8 }}>
          Enter the 6-digit code from your app to confirm:
        </div>
        <input
          type="text" value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000" maxLength={6}
          style={{ ...inputStyle(!!error), fontSize: 20, fontFamily: mono, padding: '12px', width: 200, textAlign: 'center', letterSpacing: 8, display: 'block' }}
        />
        {error && <div style={{ fontSize: 11, color: red, marginTop: 6 }}>{error}</div>}
        <button type="submit" disabled={loading || code.length < 6} style={{ ...btnStyle(loading || code.length < 6), width: 200, marginTop: 12, display: 'block' }}>
          {loading ? 'Verifying...' : 'Enable MFA'}
        </button>
      </form>
    </div>
  );
}
